import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './TraderCashReserveSettings.css';

const API_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL) ||
  (process.env.REACT_APP_API_URL
    ? `${String(process.env.REACT_APP_API_URL).replace(/\/$/, '')}/api`
    : 'https://trading.omniscalping.com/api');

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('sessionToken')}`,
});

const BALANCE_PRESETS = [0, 500, 1000, 5000, 10000, 25000, 50000];

function presetFromValue(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return { preset: '10000', custom: '' };
  if (BALANCE_PRESETS.includes(n)) return { preset: String(n), custom: '' };
  return { preset: 'custom', custom: String(n) };
}

export default function PaperTestBalanceSettings({ compact = false, className = '' }) {
  const [preset, setPreset] = useState('10000');
  const [customUsd, setCustomUsd] = useState('');
  const [savedUsd, setSavedUsd] = useState(10000);
  const [paperMode, setPaperMode] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const { data } = await axios.get(`${API_BASE}/multi-asset/status`, { headers: authHeaders() });
      const balance = Number(data.paperTestBalanceUsd);
      const resolved = Number.isFinite(balance) ? balance : 10000;
      setSavedUsd(resolved);
      setPaperMode(data.paperMode !== false);
      const { preset: p, custom } = presetFromValue(resolved);
      setPreset(p);
      setCustomUsd(custom);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const resolveAmount = () => {
    if (preset === 'custom') return Math.max(0, parseFloat(customUsd) || 0);
    return Math.max(0, parseFloat(preset) || 0);
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const paperTestBalanceUsd = resolveAmount();
      const { data } = await axios.patch(
        `${API_BASE}/multi-asset/user/settings`,
        { paperTestBalanceUsd },
        { headers: authHeaders() }
      );
      setSavedUsd(data.paperTestBalanceUsd);
      setMessage(`Paper test balance saved: $${data.paperTestBalanceUsd} USDC.`);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className={`trader-cash-reserve paper-test-balance ${compact ? 'compact' : ''} ${className}`.trim()}>
      <h3>Paper test balance (Manual + oBot)</h3>
      <p className="trader-cash-reserve-desc">
        Simulated USDC for MAP sizing while in <strong>paper mode</strong> on Manual Trades and oBot Trading.
        Set to <strong>$0</strong> to test empty-wallet behavior. In <strong>live mode</strong>, both pages use your
        real Hyperliquid withdrawable balance instead (this setting is ignored).
      </p>
      {error && <div className="error-message trader-cash-reserve-error">{error}</div>}
      {message && <div className="trader-cash-reserve-success">{message}</div>}
      <div className="trader-cash-reserve-row">
        <label>
          Test balance
          <select value={preset} onChange={(e) => setPreset(e.target.value)}>
            {BALANCE_PRESETS.map((v) => (
              <option key={v} value={String(v)}>
                ${v.toLocaleString()}
                {v === 0 ? ' (empty wallet test)' : ''}
                {v === 10000 ? ' (default)' : ''}
              </option>
            ))}
            <option value="custom">Custom amount…</option>
          </select>
        </label>
        {preset === 'custom' && (
          <label>
            Custom (USD)
            <input
              type="number"
              min={0}
              step={1}
              value={customUsd}
              onChange={(e) => setCustomUsd(e.target.value)}
              placeholder="e.g. 2500"
            />
          </label>
        )}
        <button type="button" className="secondary-btn" disabled={busy} onClick={save}>
          {busy ? 'Saving…' : 'Save test balance'}
        </button>
      </div>
      {!message && (
        <p className="trader-cash-reserve-current">
          Saved: ${savedUsd.toLocaleString()} USDC
          {paperMode ? ' · active in paper mode' : ' · live mode uses real HL wallet'}
        </p>
      )}
    </section>
  );
}
